import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/navbar/navbar';
import Footer from '../components/footer/footer';
import CustomDropdown from '../components/CustomDropdown';
import './ApplyPartner.css';
import SEO from '../components/SEO';

function ApplyPartner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Read role, default to driver
  const role = searchParams.get('role') || 'driver';

  useEffect(() => {
    if (role !== 'driver' && role !== 'courier') {
      setSearchParams({ role: 'driver' });
    }
  }, [role, setSearchParams]);

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: 'Lagos',
    make: '',
    model: '',
    plate: '',
    vehicleType: role === 'driver' ? 'Sedan' : 'Motorcycle'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      alert("Please fill out all required fields.");
      return;
    }
    setFormSubmitted(true);
  };

  return (
    <div className="apply-page-wrapper">
      <SEO 
        title={`Apply as a ${role === 'driver' ? 'Driver' : 'Courier'} - Partner Program`}
        description="Submit your driver or courier logistics application to join 9tro Ride. Earn 100% of your trip fares."
        url="https://www.9troride.com/apply"
      />
      <Navbar showSubNavbar={false} />

      <section className="apply-hero">
        <div className="apply-hero-container">
          <span className="hero-tag">Partner Program</span>
          <h1>Start earning with 9troride</h1>
          <p>Complete the registration form below. Our operations team will verify your vehicle and credentials within 48 hours.</p>
        </div>
      </section>

      <section className="apply-form-section">
        <div className="apply-form-container">
          <div className="apply-toggle-box">
            <button 
              className={`toggle-btn ${role === 'driver' ? 'active' : ''}`}
              onClick={() => { setSearchParams({ role: 'driver' }); setFormData({...formData, vehicleType: 'Sedan'}); }}
            >
              🚗 Driver Partner
            </button>
            <button 
              className={`toggle-btn ${role === 'courier' ? 'active' : ''}`}
              onClick={() => { setSearchParams({ role: 'courier' }); setFormData({...formData, vehicleType: 'Motorcycle'}); }}
            >
              🏍️ Courier dispatcher
            </button>
          </div>

          <div className="apply-card">
            {formSubmitted ? (
              <div className="apply-success-box">
                <span className="success-icon">🎉</span>
                <h2>Application Submitted!</h2>
                <p>Thank you for applying to become a 9tro partner. We have recorded your information and our dispatch team will reach out to you at <strong>{formData.phone}</strong> to schedule your vehicle inspection and activation.</p>
                <button className="back-btn" onClick={() => navigate('/')}>Return Home</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="partner-form">
                <h3>Personal Information</h3>
                <div className="form-grid-row">
                  <div className="form-group">
                    <label htmlFor="pFirst">First Name</label>
                    <input 
                      type="text" 
                      id="pFirst" 
                      required 
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="pLast">Last Name</label>
                    <input 
                      type="text" 
                      id="pLast" 
                      required 
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-grid-row">
                  <div className="form-group">
                    <label htmlFor="pEmail">Email Address</label>
                    <input 
                      type="email" 
                      id="pEmail" 
                      required 
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="pPhone">Phone Number</label>
                    <input 
                      type="tel" 
                      id="pPhone" 
                      placeholder="e.g. +234..."
                      required 
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="pCity">Operating City</label>
                  <CustomDropdown 
                    options={[
                      { value: "Lagos", label: "Lagos" },
                      { value: "Abuja", label: "Abuja" },
                      { value: "Port Harcourt", label: "Port Harcourt" },
                      { value: "Ibadan", label: "Ibadan" }
                    ]}
                    value={formData.city}
                    onChange={(val) => setFormData({...formData, city: val})}
                  />
                </div>

                <hr className="form-divider" />

                <h3>Vehicle & License Details</h3>
                <div className="form-grid-row">
                  <div className="form-group">
                    <label htmlFor="vType">Vehicle Type</label>
                    {role === 'driver' ? (
                      <CustomDropdown 
                        options={[
                          { value: "Sedan", label: "Classic Sedan" },
                          { value: "SUV", label: "Luxury SUV" },
                          { value: "Minivan", label: "Commercial Minivan" }
                        ]}
                        value={formData.vehicleType}
                        onChange={(val) => setFormData({...formData, vehicleType: val})}
                      />
                    ) : (
                      <CustomDropdown 
                        options={[
                          { value: "Motorcycle", label: "Motorbike Dispatcher" },
                          { value: "Bicycle", label: "Eco Bicycle" },
                          { value: "Van", label: "Small Delivery Van" }
                        ]}
                        value={formData.vehicleType}
                        onChange={(val) => setFormData({...formData, vehicleType: val})}
                      />
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="vMake">Vehicle Make (e.g. Toyota, Honda)</label>
                    <input 
                      type="text" 
                      id="vMake" 
                      required 
                      value={formData.make}
                      onChange={(e) => setFormData({...formData, make: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-grid-row">
                  <div className="form-group">
                    <label htmlFor="vModel">Vehicle Model (e.g. Corolla, Civic)</label>
                    <input 
                      type="text" 
                      id="vModel" 
                      required 
                      value={formData.model}
                      onChange={(e) => setFormData({...formData, model: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="vPlate">License Plate Number</label>
                    <input 
                      type="text" 
                      id="vPlate" 
                      placeholder="e.g. KJA-123AA"
                      required 
                      value={formData.plate}
                      onChange={(e) => setFormData({...formData, plate: e.target.value})}
                    />
                  </div>
                </div>

                <button type="submit" className="submit-application-btn">Submit Application</button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ApplyPartner;
